import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { IconLock } from '@tabler/icons-react'
import CatalogHeader from './components/CatalogHeader'
import CategoryScreen from './components/CategoryScreen'
import EarnPage from './components/EarnPage'
import HomeHub from './components/HomeHub'
import FlowScreenContainer from './components/FlowScreenContainer'
import GiftsGrid from './components/GiftsGrid'
import IntroScreen from './components/IntroScreen'
import NavBar from './components/NavBar'
import ProfilePage from './components/ProfilePage'
import AboutScreen from './components/AboutScreen'
import HistoryScreen from './components/HistoryScreen'
import PromoScreen from './components/PromoScreen'
import { ToastProvider } from './components/Toast'
import * as adminApi from './lib/adminApi'
import { formatBalance, parseBalanceFromUrl } from './lib/balance'
import { debugLog, isDesktopPlatform } from './lib/debug'
import { scheduleIdleTask } from './lib/idle'
import { shouldShowPublicNav } from './lib/navigation'
import { getWebApp, isTelegramWebApp, telegramAuthHeaders } from './lib/telegram'
import { useIsMounted } from './lib/useIsMounted'
import {
  preloadLottiePlayer,
  preloadOrderForm,
  preloadOrderSuccess,
  preloadPremiumOrderForm,
  preloadRentCatalog,
  preloadRentCollectionGifts,
  preloadRentDetail,
  preloadRentPay,
  preloadRentHub,
  preloadMyRentals,
  preloadUsernameCatalog,
  preloadUsernameDetail,
  preloadUsernameCard,
  preloadNumberCatalog,
  preloadNumberDetail,
  preloadNumberCard,
  preloadStarsOrderForm,
  preloadTopUpModal,
  preloadAdminGate,
} from './lib/preload'
import { initTelegramWebAppSafe, safeSheetOpenHaptic, safeShowAlert } from './lib/safeTelegram'
import { bindTelegramSafeAreaInsets } from './lib/telegramSafeArea'

const OrderForm = lazy(() => preloadOrderForm())
const StarsOrderForm = lazy(() => preloadStarsOrderForm())
const PremiumOrderForm = lazy(() => preloadPremiumOrderForm())
const TopUpModal = lazy(() => preloadTopUpModal())
const OrderSuccess = lazy(() => preloadOrderSuccess())
const RentCatalogScreen = lazy(() => preloadRentCatalog())
const RentCollectionGiftsScreen = lazy(() => preloadRentCollectionGifts())
const RentDetailScreen = lazy(() => preloadRentDetail())
const RentPayScreen = lazy(() => preloadRentPay())
const RentHubScreen = lazy(() => preloadRentHub())
const MyRentalsScreen = lazy(() => preloadMyRentals())
const UsernameCatalogScreen = lazy(() => preloadUsernameCatalog())
const UsernameDetailScreen = lazy(() => preloadUsernameDetail())
const NumberCatalogScreen = lazy(() => preloadNumberCatalog())
const NumberDetailScreen = lazy(() => preloadNumberDetail())
const AdminGate = lazy(() => preloadAdminGate())
const BOT_URL = 'https://t.me/PatrickStarRobot'

// Rent-флоу типы просмотра. Экраны заказа/оплаты (rent-detail,
// rent-*-detail, rent-*-pay) сюда НЕ входят, но сам список используется для
// управления поведением переходов внутри rental-флоу.
const RENT_NAV_TYPES = new Set([
  'rent-hub',
  'rent-catalog',
  'rent-collection-gifts',
  'rent-usernames',
  'rent-numbers',
  'my-rentals',
])

function FlowLoading() {
  return (
    <div className="flex min-h-0 w-full flex-1 items-center justify-center" role="status" aria-live="polite">
      <span className="text-[14px] font-medium text-[var(--tma-text-secondary)]">Загрузка…</span>
    </div>
  )
}

function normalizeUsername(value) {
  return String(value ?? '').trim().replace(/^@+/, '')
}

function blockDevToolsShortcuts(event) {
  if (isDesktopPlatform()) return

  if (event.key === 'F12' || event.key === 'F5') {
    event.preventDefault()
    return
  }

  const key = event.key.toLowerCase()

  if (event.ctrlKey && key === 'u') {
    event.preventDefault()
    return
  }

  if (event.ctrlKey && event.shiftKey && (key === 'i' || key === 'j')) {
    event.preventDefault()
  }
}

function BrowserStub() {
  return (
    <div className="fixed inset-0 z-[100] flex min-h-screen items-center justify-center bg-[var(--color-card)] px-6">
      <div className="max-w-sm text-center">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-accent/15 text-blue-500">
          <IconLock size={32} stroke={1.75} />
        </div>
        <h1 className="text-2xl font-black text-[var(--color-text)]">Доступно только в Telegram</h1>
        <p className="mt-3 text-[var(--color-text-secondary)]">
          Этот магазин работает только внутри мини-приложения Telegram.
        </p>
        <a
          href={BOT_URL}
          className="mt-8 inline-flex w-full items-center justify-center rounded-2xl bg-accent px-6 py-4 text-base font-semibold text-white shadow-lg shadow-accent/25 transition-transform active:scale-[0.98]"
        >
          Открыть бота
        </a>
      </div>
    </div>
  )
}

// Экран полной блокировки витрины для забаненного юзера. Показывается вместо
// всего app-shell (вход в магазин закрыт). Бан определяется бэком в
// /api/webapp-profile (is_banned/ban_comment) — это первый запрос на старте.
// Hard-замок на платящих эндпоинтах (reject_if_banned) — отдельный слой защиты
// на случай обхода этого экрана.
function BannedScreen({ comment, supportLink }) {
  return (
    <div className="fixed inset-0 z-[100] flex min-h-screen items-center justify-center bg-[var(--color-card)] px-6">
      <div className="max-w-sm text-center">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-500/15 text-red-500">
          <IconLock size={32} stroke={1.75} />
        </div>
        <h1 className="text-2xl font-black text-[var(--color-text)]">Аккаунт заблокирован</h1>
        <p className="mt-3 text-[var(--color-text-secondary)]">
          Доступ к магазину закрыт. Покупки и пополнения недоступны.
        </p>
        {comment ? (
          <p className="mt-4 rounded-2xl bg-red-500/10 px-4 py-3 text-sm text-[var(--color-text)]">
            {comment}
          </p>
        ) : null}
        <a
          href={supportLink || BOT_URL}
          className="mt-8 inline-flex w-full items-center justify-center rounded-2xl bg-accent px-6 py-4 text-base font-semibold text-white shadow-lg shadow-accent/25 transition-transform active:scale-[0.98]"
        >
          Связаться с поддержкой
        </a>
      </div>
    </div>
  )
}

export default function App() {
  const isTelegram = useMemo(() => isTelegramWebApp(), [])
  const [gifts, setGifts] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [giftsError, setGiftsError] = useState(null)
  const [flowStack, setFlowStack] = useState([])
  const [returnTab, setReturnTab] = useState('gifts')
  const [submitError, setSubmitError] = useState(null)
  const [balance, setBalance] = useState(() => parseBalanceFromUrl())
  const [paymentMethods, setPaymentMethods] = useState([])
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(true)
  const [paymentMethodsError, setPaymentMethodsError] = useState(null)
  const [isUserDataLoading, setIsUserDataLoading] = useState(true)
  const [showIntro, setShowIntro] = useState(true)
  const [activeTab, setActiveTab] = useState('gifts')
  const [activeCategory, setActiveCategory] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [profileError, setProfileError] = useState(null)
  const [needsBottomInset, setNeedsBottomInset] = useState(false)
  // Админка: шестерёнку показываем ТОЛЬКО после подтверждения whoami (по умолч. нет).
  // При флаге ADMIN_WEBAPP_ENABLED=off whoami даёт 404 → isAdmin=false → шестерёнки нет.
  const [isAdmin, setIsAdmin] = useState(false)
  const [showAdmin, setShowAdmin] = useState(false)

  const paymentOrderRef = useRef(null)
  const scrollContainerRef = useRef(null)
  const isMountedRef = useIsMounted()

  const formattedBalance = useMemo(() => formatBalance(balance), [balance])
  const isDataLoaded = !isUserDataLoading && !isLoading
  const isFlowActive = flowStack.length > 0
  const topFlow = flowStack[flowStack.length - 1] ?? null
  const isRentNavFlow = RENT_NAV_TYPES.has(topFlow?.type)
  const isArchiveGifts = !isFlowActive && activeCategory === 'gifts'
  const hasPersistentNav = (!isFlowActive && !isArchiveGifts) || topFlow?.type === 'about'

  const resetFlow = useCallback(() => {
    setFlowStack([])
    paymentOrderRef.current = null
    setSubmitError(null)
    setActiveTab((currentTab) => returnTab || currentTab)
  }, [returnTab])

  const replaceFlow = useCallback((nextStack) => {
    setFlowStack(nextStack)
  }, [])

  const pushFlow = useCallback((entry) => {
    setFlowStack((stack) => [...stack, entry])
  }, [])

  const handleFlowBack = useCallback(() => {
    if (flowStack.length <= 1) {
      resetFlow()
      return
    }
    setSubmitError(null)
    setFlowStack((stack) => stack.slice(0, -1))
  }, [flowStack.length, resetFlow])

  const fetchUserProfile = useCallback(async () => {
    if (!isTelegram) return

    const tg = getWebApp()
    try {
      const response = await fetch('/api/webapp-profile', { headers: telegramAuthHeaders(tg) })
      const result = await response.json().catch(() => ({}))

      if (!response.ok || !result.ok) {
        throw new Error(result.error || 'Не удалось загрузить профиль')
      }

      if (isMountedRef.current) {
        setUserProfile(result)
        setProfileError(null)
        if (typeof result.balance === 'number') {
          setBalance(result.balance)
        }
      }
    } catch (error) {
      if (isMountedRef.current) {
        setProfileError(error.message || 'Не удалось загрузить профиль')
      }
    } finally {
      if (isMountedRef.current) {
        setIsUserDataLoading(false)
      }
    }
  }, [isTelegram, isMountedRef])

  const fetchUserData = fetchUserProfile

  const fetchPaymentMethods = useCallback(async () => {
    if (!isTelegram) return

    if (isMountedRef.current) {
      setPaymentMethodsLoading(true)
      setPaymentMethodsError(null)
    }

    try {
      const tg = getWebApp()
      const response = await fetch('/api/webapp-payment-methods', { headers: telegramAuthHeaders(getWebApp()) })
      const result = await response.json().catch(() => ({}))

      if (!response.ok || !result.ok) {
        throw new Error(result.error || 'Не удалось загрузить способы оплаты')
      }

      if (isMountedRef.current) {
        setPaymentMethods(Array.isArray(result.methods) ? result.methods : [])
      }
    } catch (error) {
      if (isMountedRef.current) {
        setPaymentMethods([])
        setPaymentMethodsError(error.message || 'Не удалось загрузить способы оплаты')
      }
    } finally {
      if (isMountedRef.current) {
        setPaymentMethodsLoading(false)
      }
    }
  }, [isTelegram, isMountedRef])

  const webApp = useMemo(() => getWebApp(), [])

  const selfUsername = useMemo(
    () => normalizeUsername(webApp.initDataUnsafe?.user?.username || ''),
    [webApp],
  )

  const handlePaymentSuccess = useCallback((giftName) => {
    const currentOrder = paymentOrderRef.current
    const resolvedName = giftName || currentOrder?.giftName || ''
    const product = currentOrder?.product || null
    const starsAmount = currentOrder?.starsAmount ?? null
    const premiumMonths = currentOrder?.premiumMonths ?? null
    const recipientUsername = currentOrder?.recipientUsername ?? ''
    debugLog('handlePaymentSuccess', { giftName: resolvedName, product })
    paymentOrderRef.current = null
    void preloadOrderSuccess()
    replaceFlow([
      {
        type: 'success',
        giftName: resolvedName,
        product,
        starsAmount,
        premiumMonths,
        recipientUsername,
      },
    ])
    fetchUserData().catch((error) => {
      console.error('[WebApp] fetchUserData after payment failed', error)
    })
  }, [fetchUserData, replaceFlow])

  const handleTopUpPaid = useCallback(() => {
    fetchUserData().catch((error) => {
      console.error('[WebApp] fetchUserData after top-up failed', error)
    })
  }, [fetchUserData])

  const handleTopUpSuccess = useCallback(() => {
    handlePaymentSuccess(paymentOrderRef.current?.giftName)
  }, [handlePaymentSuccess])

  useEffect(() => {
    const handleContextMenu = (event) => {
      event.preventDefault()
    }
    document.addEventListener('contextmenu', handleContextMenu)
    document.addEventListener('keydown', blockDevToolsShortcuts)

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu)
      document.removeEventListener('keydown', blockDevToolsShortcuts)
    }
  }, [])

  // Тихая проверка «я админ?»: whoami под no_session-гардом (флаг on + initData +
  // whitelist). 200→показать шестерёнку. Не-200 (вкл. 404 при флаге off)→скрыть.
  useEffect(() => {
    if (!isTelegram) return undefined
    let alive = true
    adminApi.whoami().then((ok) => {
      if (alive) setIsAdmin(ok)
    })
    return () => {
      alive = false
    }
  }, [isTelegram])

  useEffect(() => {
    if (!isTelegram) return undefined

    initTelegramWebAppSafe()
    return bindTelegramSafeAreaInsets()
  }, [isTelegram])

  useEffect(() => {
    if (!isTelegram) return undefined

    fetchUserData()
    fetchPaymentMethods()

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchUserData()
      }
    }

    window.addEventListener('focus', fetchUserData)
    window.addEventListener('visibilitychange', handleVisibilityChange)

    const tg = window.Telegram?.WebApp
    if (tg?.onEvent) {
      tg.onEvent('viewportChanged', fetchUserData)
    }

    return () => {
      window.removeEventListener('focus', fetchUserData)
      window.removeEventListener('visibilitychange', handleVisibilityChange)
      if (tg?.offEvent) {
        tg.offEvent('viewportChanged', fetchUserData)
      }
    }
  }, [isTelegram, fetchUserData, fetchPaymentMethods])

  useLayoutEffect(() => {
    if (isFlowActive && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0
    }
  }, [isFlowActive])

  useLayoutEffect(() => {
    const node = scrollContainerRef.current
    if (!node) {
      return undefined
    }

    let frameId = 0
    const measure = () => {
      cancelAnimationFrame(frameId)
      frameId = requestAnimationFrame(() => {
        if (!hasPersistentNav) {
          setNeedsBottomInset(false)
          return
        }

        const scrollNode = isFlowActive ? node.querySelector('.flow-screen-scroll') : node
        const navNode = document.querySelector('nav[aria-label="Навигація"]')
        if (!scrollNode || !navNode) {
          setNeedsBottomInset(false)
          return
        }

        const scrollRect = scrollNode.getBoundingClientRect()
        const navRect = navNode.getBoundingClientRect()
        const unobscuredHeight = Math.max(0, Math.min(scrollNode.clientHeight, navRect.top - scrollRect.top))
        let contentBottom = 0
        for (const child of scrollNode.children) {
          if (getComputedStyle(child).display === 'none') continue
          const childRect = child.getBoundingClientRect()
          contentBottom = Math.max(contentBottom, childRect.bottom - scrollRect.top + scrollNode.scrollTop)
        }
        const shouldInset = contentBottom > unobscuredHeight + 1
        setNeedsBottomInset((current) => (current === shouldInset ? current : shouldInset))
      })
    }

    const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null
    resizeObserver?.observe(node)
    for (const child of node.children) {
      resizeObserver?.observe(child)
    }
    const mutationObserver = typeof MutationObserver === 'function' ? new MutationObserver(measure) : null
    mutationObserver?.observe(node, { childList: true, subtree: true, characterData: true })
    window.addEventListener('resize', measure)
    measure()

    return () => {
      cancelAnimationFrame(frameId)
      resizeObserver?.disconnect()
      mutationObserver?.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [isFlowActive, hasPersistentNav, activeTab, activeCategory, isDataLoaded, gifts.length, topFlow?.type])

  useEffect(() => {
    if (!isTelegram) return undefined

    let cancelled = false

    async function loadGifts() {
      setIsLoading(true)
      setGiftsError(null)

      try {
        const response = await fetch('/api/gifts')
        const result = await response.json().catch(() => ({}))

        if (!response.ok || !result.ok) {
          throw new Error(result.error || 'Не удалось загрузить подарки')
        }

        if (!cancelled) {
          setGifts(Array.isArray(result.gifts) ? result.gifts : [])
        }
      } catch (error) {
        if (!cancelled) {
          setGifts([])
          setGiftsError(error.message || 'Не удалось загрузить подарки')
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    loadGifts()

    return () => {
      cancelled = true
    }
  }, [isTelegram])

  useEffect(() => {
    if (!isTelegram || isLoading) return undefined

    return scheduleIdleTask(() => {
      preloadOrderForm()
      preloadTopUpModal()
    })
  }, [isTelegram, isLoading])

  useEffect(() => {
    if (!isTelegram) return undefined

    const backButton = window.Telegram?.WebApp?.BackButton
    if (!backButton) return undefined

    const handleBack = () => {
      if (isFlowActive) {
        handleFlowBack()
        return
      }

      if (activeCategory) {
        setActiveCategory(null)
      }
    }

    const showBackButton = isFlowActive || activeCategory !== null

    if (showBackButton) {
      try {
        backButton.show?.()
      } catch (error) {
        console.error('[WebApp] BackButton.show failed', error)
      }

      if (typeof backButton.onClick === 'function') {
        backButton.onClick(handleBack)
      }
    } else {
      try {
        backButton.hide?.()
      } catch (error) {
        console.error('[WebApp] BackButton.hide failed', error)
      }

      if (typeof backButton.offClick === 'function') {
        backButton.offClick(handleBack)
      }
    }

    return () => {
      try {
        backButton.hide?.()
      } catch (error) {
        console.error('[WebApp] BackButton.hide cleanup failed', error)
      }

      if (typeof backButton.offClick === 'function') {
        backButton.offClick(handleBack)
      }
    }
  }, [isTelegram, isFlowActive, activeCategory, handleFlowBack])

  const startPurchase = useCallback((gift) => {
    setSubmitError(null)
    setReturnTab(activeTab)
    setFlowStack([{ type: 'order', gift }])
    void preloadOrderForm().catch((error) => console.error('[WebApp] preload order form failed', error))
  }, [activeTab])

  const startStarsPurchase = useCallback(() => {
    setSubmitError(null)
    setReturnTab(activeTab)
    setFlowStack([{ type: 'stars-order' }])
    void preloadStarsOrderForm().catch((error) => console.error('[WebApp] preload stars form failed', error))
  }, [activeTab])

  const startPremiumPurchase = useCallback(() => {
    setSubmitError(null)
    setReturnTab(activeTab)
    setFlowStack([{ type: 'premium-order' }])
    void preloadPremiumOrderForm().catch((error) => console.error('[WebApp] preload premium form failed', error))
  }, [activeTab])

  const handlePreloadOrderForm = useCallback((gift) => {
    void preloadOrderForm().catch((error) => console.warn('[WebApp] background order preload failed', error))
    void preloadLottiePlayer().catch((error) => console.warn('[WebApp] background Lottie preload failed', error))
    if (gift?.animation_url) {
      void fetch(gift.animation_url, { cache: 'force-cache' }).catch(() => {})
    }
  }, [])

  const handleTopUp = useCallback(() => {
    paymentOrderRef.current = null
    setSubmitError(null)
    setReturnTab(activeTab)
    setFlowStack([{ type: 'topup', mode: 'topup' }])
    void preloadTopUpModal().catch((error) => console.error('[WebApp] preload top-up failed', error))
  }, [activeTab])

  // Тап шестерёнки: тянем admin-чанк лениво (preload) и открываем гейт.
  const handleOpenAdmin = useCallback(() => {
    void preloadAdminGate().then(() => {
      if (isMountedRef.current) setShowAdmin(true)
    })
  }, [isMountedRef])

  // Deep-link: если URL содержит ?screen=topup, сразу открыть пополнение;
  // ?screen=profile — вкладка профиля; ?screen=stars/premium — покупка звёзд/Premium.
  // Выполняется РОВНО ОДИН РАЗ при монтировании: колбэки зависят от activeTab и
  // пересоздаются при навигации, поэтому без guard эффект повторно читал бы тот же
  // ?screen и насильно возвращал пользователя в исходный раздел.
  const deepLinkHandledRef = useRef(false)
  useEffect(() => {
    if (deepLinkHandledRef.current) return
    deepLinkHandledRef.current = true

    // Диспетчер: screen-идентификатор → действие. Единый для обоих каналов
    // (startapp-параметр и ?screen=-URL), чтобы маршрутизация не двоилась.
    const openScreen = (screen) => {
      if (screen === 'topup') handleTopUp()
      else if (screen === 'profile') setActiveTab('profile')
      else if (screen === 'stars') startStarsPurchase()
      else if (screen === 'premium') startPremiumPurchase()
      else if (screen === 'rent') startRent()
      else if (screen === 'gifts') setActiveCategory('gifts')
      // иначе — ничего: остаёмся на главной (HomeHub)
    }

    // 1) start_param (ПОДПИСАННЫЙ канал startapp-ссылок) — приоритет над ?screen=.
    //    Формат u<label>_a<action> зеркалит бот (_parse_deeplink_payload). Метку в
    //    статистику пишет БЭК из start_param (_record_webapp_utm) — фронт только
    //    маршрутизирует категорию. ref<id>/сырой label/мусор → главная.
    let routed = false
    try {
      const sp = getWebApp().initDataUnsafe?.start_param
      if (sp && typeof sp === 'string') {
        const m = /^u[a-zA-Z0-9]+_a(S|P|TON|DEP|NFT)$/.exec(sp)
        if (m) {
          // TON/DEP сознательно не ведём в витрину из ссылки → главная (метка всё равно пишется).
          const screen = { S: 'stars', P: 'premium', NFT: 'gifts' }[m[1]]
          if (screen) openScreen(screen)
          routed = true
        }
      }
    } catch {
      // кривой WebApp/параметр не должен ронять витрину — тихо остаёмся на главной
    }

    // 2) ?screen= (СТАРЫЙ канал из URL — кнопки web_app в боте). Fallback, только
    //    если start_param не дал структурного маршрута. Кнопки бота не ломаем.
    if (!routed) {
      const screen = new URLSearchParams(window.location.search).get('screen')
      if (screen) openScreen(screen)
    }
    // startRent в депсы НЕ добавляем: он объявлен ниже (TDZ), а guard выше
    // гарантирует единственный запуск — массив зависимостей роли не играет.
  }, [handleTopUp, startStarsPurchase, startPremiumPurchase])

  const handleOpenAbout = useCallback(() => {
    setReturnTab(activeTab)
    setFlowStack([{ type: 'about' }])
  }, [activeTab])

  const handleOpenHistory = useCallback(() => {
    setReturnTab(activeTab)
    setFlowStack([{ type: 'history' }])
  }, [activeTab])

  const handleOpenPromo = useCallback(() => {
    setReturnTab(activeTab)
    setFlowStack([{ type: 'promo' }])
  }, [activeTab])

  const handleOpenGifts = useCallback(() => {
    setActiveCategory('gifts')
  }, [])

  const handleOpenStars = useCallback(() => {
    startStarsPurchase()
  }, [startStarsPurchase])

  const handleOpenPremium = useCallback(() => {
    startPremiumPurchase()
  }, [startPremiumPurchase])

  const startRent = useCallback(() => {
    setSubmitError(null)
    setReturnTab(activeTab)
    setFlowStack([{ type: 'rent-hub' }])
    void preloadRentHub().catch((error) => console.error('[WebApp] preload rent hub failed', error))
    // Вторичные экраны прогреваем после первого кадра хаба, чтобы не
    // конкурировать с его чанком и Lottie за сеть при клике.
    scheduleIdleTask(() => {
      void preloadRentCatalog().catch((error) => console.warn('[WebApp] preload rent catalog failed', error))
      void preloadRentCollectionGifts().catch((error) => console.warn('[WebApp] preload rent gifts failed', error))
      void preloadUsernameCatalog().catch((error) => console.warn('[WebApp] preload username catalog failed', error))
      void preloadNumberCatalog().catch((error) => console.warn('[WebApp] preload number catalog failed', error))
      void preloadMyRentals().catch((error) => console.warn('[WebApp] preload rentals failed', error))
    }, { timeout: 1800 })
  }, [activeTab])

  // Хаб → каталог аренды подарков (push, свайп-назад вернёт в хаб)
  const handleOpenGiftRent = useCallback(() => {
    setSubmitError(null)
    pushFlow({ type: 'rent-catalog' })
    void preloadRentCatalog().catch((error) => console.error('[WebApp] preload rent catalog failed', error))
  }, [pushFlow])

  // Хаб → управление арендой (все типы)
  const handleOpenManageRentals = useCallback(() => {
    setSubmitError(null)
    pushFlow({ type: 'my-rentals' })
    void preloadMyRentals().catch((error) => console.error('[WebApp] preload rentals failed', error))
  }, [pushFlow])

  // ───────────────────── Аренда юзернеймов (Этап 2) ─────────────────────
  // Зеркало NFT-флоу, но на шаг короче: каталог сразу плоский (без коллекций).

  // Хаб → каталог юзернеймов (push, свайп-назад вернёт в хаб)
  const handleOpenUsernameRent = useCallback(() => {
    setSubmitError(null)
    pushFlow({ type: 'rent-usernames' })
    void preloadUsernameCatalog().catch((error) => console.error('[WebApp] preload username catalog failed', error))
    scheduleIdleTask(() => {
      void preloadUsernameCard().catch((error) => console.warn('[WebApp] preload username card failed', error))
      void preloadUsernameDetail().catch((error) => console.warn('[WebApp] preload username detail failed', error))
    }, { timeout: 1200 })
  }, [pushFlow])

  // Каталог → карточка юзернейма (push, свайп-назад вернёт в каталог)
  const handleOpenUsernameDetail = useCallback((item) => {
    if (!item) return
    setSubmitError(null)
    pushFlow({ type: 'rent-username-detail', item })
    void preloadUsernameDetail().catch((error) => console.error('[WebApp] preload username detail failed', error))
  }, [pushFlow])

  // Карточка → создание заказа → экран оплаты. Маппинг ошибок идентичен NFT
  // (handleCreateRentOrder); меняется только эндпойнт и форма rentOrder.
  const handleCreateUsernameOrder = useCallback(async (item, durationDays) => {
    if (!item?.nft_address) {
      const message = 'Юзернейм недоступен для аренды'
      setSubmitError(message)
      safeShowAlert(message)
      throw new Error(message)
    }

    let tg
    try {
      tg = getWebApp()
    } catch (error) {
      console.error('[WebApp] getWebApp failed', error)
      safeShowAlert('Telegram WebApp недоступен')
      throw error
    }

    setSubmitError(null)

    try {
      debugLog('handleCreateUsernameOrder', 'POST /api/create-username-rent-order', {
        nft_address: item.nft_address,
        durationDays,
      })
      const response = await fetch('/api/create-username-rent-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          initData: tg.initData || '',
          nft_address: item.nft_address,
          duration_days: durationDays,
        }),
      })

      const result = await response.json().catch((parseError) => {
        console.error('[WebApp] create-username-rent-order JSON parse failed', parseError)
        return {}
      })

      debugLog('handleCreateUsernameOrder', 'create-username-rent-order response', {
        status: response.status,
        result,
      })

      if (response.status === 400 && result.error === 'invalid_duration') {
        throw new Error(
          result.message || `Допустимый срок: ${result.min_days}–${result.max_days} дн.`,
        )
      }
      if (response.status === 409 && result.error === 'not_rentable') {
        throw new Error('Этот юзернейм сейчас нельзя арендовать')
      }
      if (response.status === 404) {
        throw new Error('Юзернейм не найден, обновите каталог')
      }
      if (response.status === 502) {
        throw new Error('Маркет временно недоступен, попробуйте позже')
      }
      if (!response.ok || !result.ok) {
        throw new Error(result.message || result.error || 'Не удалось оформить аренду')
      }

      const rentOrder = {
        orderId: result.order_id,
        totalPrice: result.total_price,
        pricePerDayRub: result.price_per_day_rub,
        durationDays: result.duration_days,
        itemName: result.item_name,
        iconUrl: result.icon_url,
        animationUrl: '', // у юзернеймов нет Lottie — статичный экран успеха
        rentType: 'username', // экран успеха: «Юзернейм арендован» вместо «Подарок»
      }

      if (!isMountedRef.current) return

      await preloadRentPay()

      if (!isMountedRef.current) return

      pushFlow({ type: 'rent-username-pay', rentOrder })
    } catch (error) {
      console.error('[WebApp] handleCreateUsernameOrder failed', error)
      const message = error?.message || 'Ошибка оформления аренды'
      setSubmitError(message)
      safeShowAlert(message)
      throw error
    }
  }, [isMountedRef, pushFlow])

  // ───────────────────── Аренда номеров (Этап 3) ─────────────────────
  // Зеркало username-флоу: каталог сразу плоский, без коллекций.

  // Хаб → каталог номеров (push, свайп-назад вернёт в хаб)
  const handleOpenNumberRent = useCallback(() => {
    setSubmitError(null)
    pushFlow({ type: 'rent-numbers' })
    void preloadNumberCatalog().catch((error) => console.error('[WebApp] preload number catalog failed', error))
    scheduleIdleTask(() => {
      void preloadNumberCard().catch((error) => console.warn('[WebApp] preload number card failed', error))
      void preloadNumberDetail().catch((error) => console.warn('[WebApp] preload number detail failed', error))
    }, { timeout: 1200 })
  }, [pushFlow])

  // Каталог → карточка номера (push, свайп-назад вернёт в каталог)
  const handleOpenNumberDetail = useCallback((item) => {
    if (!item) return
    setSubmitError(null)
    pushFlow({ type: 'rent-number-detail', item })
    void preloadNumberDetail().catch((error) => console.error('[WebApp] preload number detail failed', error))
  }, [pushFlow])

  // Карточка → создание заказа → экран оплаты. Маппинг ошибок идентичен username;
  // меняется только эндпойнт и rentType.
  const handleCreateNumberOrder = useCallback(async (item, durationDays) => {
    if (!item?.nft_address) {
      const message = 'Номер недоступен для аренды'
      setSubmitError(message)
      safeShowAlert(message)
      throw new Error(message)
    }

    let tg
    try {
      tg = getWebApp()
    } catch (error) {
      console.error('[WebApp] getWebApp failed', error)
      safeShowAlert('Telegram WebApp недоступен')
      throw error
    }

    setSubmitError(null)

    try {
      debugLog('handleCreateNumberOrder', 'POST /api/create-number-rent-order', {
        nft_address: item.nft_address,
        durationDays,
      })
      const response = await fetch('/api/create-number-rent-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          initData: tg.initData || '',
          nft_address: item.nft_address,
          duration_days: durationDays,
        }),
      })

      const result = await response.json().catch((parseError) => {
        console.error('[WebApp] create-number-rent-order JSON parse failed', parseError)
        return {}
      })

      debugLog('handleCreateNumberOrder', 'create-number-rent-order response', {
        status: response.status,
        result,
      })

      if (response.status === 400 && result.error === 'invalid_duration') {
        throw new Error(
          result.message || `Допустимый срок: ${result.min_days}–${result.max_days} дн.`,
        )
      }
      if (response.status === 409 && result.error === 'not_rentable') {
        throw new Error('Этот номер сейчас нельзя арендовать')
      }
      if (response.status === 404) {
        throw new Error('Номер не найден, обновите каталог')
      }
      if (response.status === 502) {
        throw new Error('Маркет временно недоступен, попробуйте позже')
      }
      if (!response.ok || !result.ok) {
        throw new Error(result.message || result.error || 'Не удалось оформить аренду')
      }

      const rentOrder = {
        orderId: result.order_id,
        totalPrice: result.total_price,
        pricePerDayRub: result.price_per_day_rub,
        durationDays: result.duration_days,
        itemName: result.item_name,
        iconUrl: result.icon_url,
        animationUrl: '', // у номеров нет Lottie — статичный экран успеха
        rentType: 'number', // экран успеха: «Номер арендован» вместо «Подарок»
      }

      if (!isMountedRef.current) return

      await preloadRentPay()

      if (!isMountedRef.current) return

      pushFlow({ type: 'rent-number-pay', rentOrder })
    } catch (error) {
      console.error('[WebApp] handleCreateNumberOrder failed', error)
      const message = error?.message || 'Ошибка оформления аренды'
      setSubmitError(message)
      safeShowAlert(message)
      throw error
    }
  }, [isMountedRef, pushFlow])

  // С экрана успеха аренды → «Мои аренды». Стек = хаб → my-rentals,
  // чтобы свайп/кнопка «назад» вернули в хаб аренды, а не на экран успеха.
  const handleSuccessManageRentals = useCallback(() => {
    replaceFlow([{ type: 'rent-hub' }, { type: 'my-rentals' }])
    void preloadMyRentals().catch((error) => console.error('[WebApp] preload rentals failed', error))
  }, [replaceFlow])

  const handleSuccessBuyMore = useCallback((product) => {
    setSubmitError(null)

    if (product === 'stars') {
      startStarsPurchase()
      return
    }

    if (product === 'premium') {
      startPremiumPurchase()
      return
    }

    setActiveTab('gifts')
    setActiveCategory('gifts')
    setFlowStack([])
  }, [startPremiumPurchase, startStarsPurchase])

  // Каталог коллекций → подарки коллекции (push, свайп-назад вернёт в каталог)
  const handleOpenCollection = useCallback((collection) => {
    if (!collection?.address) return
    setSubmitError(null)
    safeSheetOpenHaptic()
    pushFlow({ type: 'rent-collection-gifts', collection })
    void preloadRentCollectionGifts().catch((error) => console.error('[WebApp] preload rent gifts failed', error))
  }, [pushFlow])

  // Каталог → карточка подарка (top-of-stack, свайп-назад вернёт в каталог)
  const handleOpenRentDetail = useCallback((gift) => {
    if (!gift) return
    setSubmitError(null)
    pushFlow({ type: 'rent-detail', gift })
    void preloadRentDetail().catch((error) => console.error('[WebApp] preload rent detail failed', error))
  }, [pushFlow])

  // Карточка → создание заказа → экран оплаты. Ошибки → submitError + throw (форма дожмёт спиннер).
  const handleCreateRentOrder = useCallback(async (gift, durationDays) => {
    if (!gift?.nft_address) {
      const message = 'Подарок недоступен для аренды'
      setSubmitError(message)
      safeShowAlert(message)
      throw new Error(message)
    }

    let tg
    try {
      tg = getWebApp()
    } catch (error) {
      console.error('[WebApp] getWebApp failed', error)
      safeShowAlert('Telegram WebApp недоступен')
      throw error
    }

    setSubmitError(null)

    try {
      debugLog('handleCreateRentOrder', 'POST /api/create-rent-order', {
        nft_address: gift.nft_address,
        collection_address: gift.collection_address,
        durationDays,
      })
      const response = await fetch('/api/create-rent-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          initData: tg.initData || '',
          nft_address: gift.nft_address,
          collection_address: gift.collection_address || '',
          duration_days: durationDays,
        }),
      })

      const result = await response.json().catch((parseError) => {
        console.error('[WebApp] create-rent-order JSON parse failed', parseError)
        return {}
      })

      debugLog('handleCreateRentOrder', 'create-rent-order response', {
        status: response.status,
        result,
      })

      if (response.status === 400 && result.error === 'invalid_duration') {
        throw new Error(
          result.message || `Допустимый срок: ${result.min_days}–${result.max_days} дн.`,
        )
      }
      if (response.status === 409 && result.error === 'not_rentable') {
        throw new Error('Этот подарок сейчас нельзя арендовать')
      }
      if (response.status === 404) {
        throw new Error('Подарок не найден, обновите каталог')
      }
      if (response.status === 502) {
        throw new Error('Маркет временно недоступен, попробуйте позже')
      }
      if (!response.ok || !result.ok) {
        throw new Error(result.message || result.error || 'Не удалось оформить аренду')
      }

      const rentOrder = {
        orderId: result.order_id,
        totalPrice: result.total_price,
        pricePerDayRub: result.price_per_day_rub,
        durationDays: result.duration_days,
        itemName: result.item_name,
        iconUrl: result.icon_url,
        animationUrl: gift?.animation_url || '', // для Lottie на экране успеха (фронт, бэк не трогаем)
        rentType: 'nft',
      }

      if (!isMountedRef.current) return

      await preloadRentPay()

      if (!isMountedRef.current) return

      pushFlow({ type: 'rent-pay', rentOrder })
    } catch (error) {
      console.error('[WebApp] handleCreateRentOrder failed', error)
      const message = error?.message || 'Ошибка оформления аренды'
      setSubmitError(message)
      safeShowAlert(message)
      throw error
    }
  }, [isMountedRef, pushFlow])

  // 402 на экране оплаты → пополнение ПОВЕРХ rent-pay (push, не setFlowStack — стек не теряем).
  // После «Готово» в TopUpModal handleFlowBack снимет topup-слой, rent-pay перемонтируется
  // со свежим balance (его обновит handleTopUpPaid через fetchUserData) → кнопка станет «Оплатить».
  const handleOpenTopUpForRent = useCallback(() => {
    setSubmitError(null)
    pushFlow({ type: 'topup', mode: 'topup' })
    void preloadTopUpModal().catch((error) => console.error('[WebApp] preload top-up failed', error))
  }, [pushFlow])

  // Успех оплаты аренды → экран успеха (ветка product='rent' из диффа 1).
  const handleRentSuccess = useCallback((payload) => {
    const itemName = payload?.itemName || ''
    const rentDurationDays = payload?.durationDays ?? null
    const rentAnimationUrl = payload?.animationUrl || ''
    const rentIconUrl = payload?.iconUrl || ''
    const rentType = payload?.rentType || 'nft'
    debugLog('handleRentSuccess', { itemName, rentDurationDays })
    void preloadOrderSuccess()
    replaceFlow([
      {
        type: 'success',
        product: 'rent',
        giftName: itemName,
        rentDurationDays,
        rentAnimationUrl,
        rentIconUrl,
        rentType,
      },
    ])
    fetchUserData().catch((error) => {
      console.error('[WebApp] fetchUserData after rent failed', error)
    })
  }, [fetchUserData, replaceFlow])

  const handlePromoSuccess = useCallback(() => {
    resetFlow()
    fetchUserData()
  }, [resetFlow, fetchUserData])

  const handleStarsPurchase = useCallback(async (orderPayload) => {
    const recipient = orderPayload?.recipientUsername
    if (!recipient) {
      const message = 'Укажите @username получателя или выберите «Купить себе».'
      setSubmitError(message)
      safeShowAlert(message)
      throw new Error(message)
    }

    let tg
    try {
      tg = getWebApp()
    } catch (error) {
      console.error('[WebApp] getWebApp failed', error)
      safeShowAlert('Telegram WebApp недоступен')
      throw error
    }

    setSubmitError(null)

    try {
      debugLog('handleStarsPurchase', 'POST /api/create-stars-order', orderPayload)

      const response = await fetch('/api/create-stars-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          initData: tg.initData || '',
          stars_amount: orderPayload.starsAmount,
          buyForSelf: orderPayload.buyForSelf,
          recipientUsername: recipient,
        }),
      })

      const result = await response.json().catch((parseError) => {
        console.error('[WebApp] create-stars-order JSON parse failed', parseError)
        return {}
      })

      debugLog('handleStarsPurchase', 'create-stars-order response', {
        ok: response.ok,
        status: response.status,
        result,
      })

      if (!response.ok || !result.ok) {
        throw new Error(result.message || result.error || 'Не удалось оформить заказ')
      }

      const readyOrder = {
        orderId: result.order_id,
        totalPrice: result.total_price,
        product: 'stars',
        starsAmount: result.stars_amount,
        recipientUsername: recipient,
        giftName: `${result.stars_amount} зв. на аккаунт @${recipient}`,
        error: null,
      }

      if (!isMountedRef.current) {
        return
      }

      paymentOrderRef.current = readyOrder

      await preloadTopUpModal()

      if (!isMountedRef.current) {
        return
      }

      pushFlow({
        type: 'topup',
        mode: 'order',
        paymentOrder: readyOrder,
      })
    } catch (error) {
      console.error('[WebApp] handleStarsPurchase failed', error)
      const message = error?.message || 'Ошибка оформления заказа'
      setSubmitError(message)
      safeShowAlert(message)
      throw error
    }
  }, [isMountedRef, pushFlow])

  const handlePremiumPurchase = useCallback(async (orderPayload) => {
    const recipient = orderPayload?.recipientUsername
    if (!recipient) {
      const message = 'Укажите @username получателя или выберите «Себе».'
      setSubmitError(message)
      safeShowAlert(message)
      throw new Error(message)
    }

    let tg
    try {
      tg = getWebApp()
    } catch (error) {
      console.error('[WebApp] getWebApp failed', error)
      safeShowAlert('Telegram WebApp недоступен')
      throw error
    }

    setSubmitError(null)

    try {
      debugLog('handlePremiumPurchase', 'POST /api/create-premium-order', orderPayload)

      const response = await fetch('/api/create-premium-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          initData: tg.initData || '',
          months: orderPayload.months,
          buyForSelf: orderPayload.buyForSelf,
          recipientUsername: recipient,
        }),
      })

      const result = await response.json().catch((parseError) => {
        console.error('[WebApp] create-premium-order JSON parse failed', parseError)
        return {}
      })

      debugLog('handlePremiumPurchase', 'create-premium-order response', {
        ok: response.ok,
        status: response.status,
        result,
      })

      if (!response.ok || !result.ok) {
        throw new Error(result.message || result.error || 'Не удалось оформить заказ')
      }

      const months = result.months ?? orderPayload.months
      const readyOrder = {
        orderId: result.order_id,
        totalPrice: result.total_price,
        product: 'premium',
        premiumMonths: months,
        recipientUsername: recipient,
        giftName: `Premium ${months} мес. → @${recipient}`,
        error: null,
      }

      if (!isMountedRef.current) {
        return
      }

      paymentOrderRef.current = readyOrder

      await preloadTopUpModal()

      if (!isMountedRef.current) {
        return
      }

      pushFlow({
        type: 'topup',
        mode: 'order',
        paymentOrder: readyOrder,
      })
    } catch (error) {
      console.error('[WebApp] handlePremiumPurchase failed', error)
      const message = error?.message || 'Ошибка оформления заказа'
      setSubmitError(message)
      safeShowAlert(message)
      throw error
    }
  }, [isMountedRef, pushFlow])

  const handlePurchase = useCallback(async (orderPayload) => {
    const recipient = orderPayload?.recipientUsername
    if (!recipient) {
      const message = 'Укажите @username получателя или выберите «Купить себе».'
      setSubmitError(message)
      safeShowAlert(message)
      throw new Error(message)
    }

    let tg
    try {
      tg = getWebApp()
    } catch (error) {
      console.error('[WebApp] getWebApp failed', error)
      safeShowAlert('Telegram WebApp недоступен')
      throw error
    }

    const giftName = orderPayload.giftName
    setSubmitError(null)

    try {
      debugLog('handlePurchase', 'POST /api/create-order', orderPayload)

      const response = await fetch('/api/create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          initData: tg.initData || '',
          order: {
            giftId: orderPayload.giftId,
            giftSlug: orderPayload.giftSlug,
            recipientUsername: recipient,
            buyForSelf: orderPayload.buyForSelf,
            hideSenderName: orderPayload.hideSenderName,
          },
        }),
      })

      const result = await response.json().catch((parseError) => {
        console.error('[WebApp] create-order JSON parse failed', parseError)
        return {}
      })

      debugLog('handlePurchase', 'create-order response', {
        ok: response.ok,
        status: response.status,
        result,
      })

      if (!response.ok || !result.ok) {
        throw new Error(result.message || result.error || 'Не удалось оформить заказ')
      }

      const readyOrder = {
        orderId: result.order_id,
        totalPrice: result.total_price,
        giftName,
        error: null,
      }

      if (!isMountedRef.current) {
        return
      }

      paymentOrderRef.current = readyOrder

      await preloadTopUpModal()

      if (!isMountedRef.current) {
        return
      }

      pushFlow({
        type: 'topup',
        mode: 'order',
        paymentOrder: readyOrder,
      })
    } catch (error) {
      console.error('[WebApp] handlePurchase failed', error)
      const message = error?.message || 'Ошибка оформления заказа'
      setSubmitError(message)
      safeShowAlert(message)
      throw error
    }
  }, [isMountedRef, replaceFlow])

  const handleIntroComplete = useCallback(() => {
    setShowIntro(false)
  }, [])

  const handleTabChange = useCallback((tabId) => {
    const inAboutFlow = topFlow?.type === 'about'
    const inRentNavFlow = RENT_NAV_TYPES.has(topFlow?.type)
    // В about- и rent-просмотр-флоу панель видна поверх флоу: тап по любому
    // табу (включая подсвеченный) должен выйти из флоу, а не упереться в
    // ранний return.
    if (!inAboutFlow && !inRentNavFlow && tabId === activeTab) return
    if (inAboutFlow || inRentNavFlow) {
      setFlowStack([])
      setSubmitError(null)
    }
    setActiveCategory(null)
    setActiveTab(tabId)
  }, [topFlow, activeTab])

  return (
    <ToastProvider>
      {isTelegram && showIntro ? (
        <IntroScreen
          isReady={isDataLoaded}
          onComplete={handleIntroComplete}
        />
      ) : null}

      {!isTelegram ? (
        <BrowserStub />
      ) : userProfile?.is_banned ? (
        <BannedScreen
          comment={userProfile.ban_comment}
          supportLink={userProfile.support_link}
        />
      ) : (
        <div className={`relative flex min-h-0 flex-1 flex-col overflow-hidden bg-[#08080A] font-sans text-[#1F2937] ${activeTab === 'profile' ? 'app-profile-canvas' : ''}`}>
          <div
            ref={scrollContainerRef}
            className={`public-app app-shell-content relative z-[1] mx-auto flex min-h-0 w-full max-w-md flex-1 flex-col px-4 ${activeTab === 'profile' ? 'app-shell-profile' : ''} ${isFlowActive ? 'app-shell-flow-active' : ''} ${hasPersistentNav ? 'app-shell-has-bottom-nav' : ''} ${needsBottomInset ? 'app-shell-needs-bottom-inset' : ''} ${
              isFlowActive
                ? 'overflow-hidden'
                : 'overflow-y-auto'
            }`}
            style={{ overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}
          >
            <div className={!isFlowActive && activeTab === 'gifts' ? 'animate-fade-in' : 'hidden'}>
              {!isArchiveGifts ? (
                <CatalogHeader
                  formattedBalance={formattedBalance}
                  onTopUp={handleTopUp}
                  isAdmin={isAdmin}
                  onOpenAdmin={handleOpenAdmin}
                />
              ) : null}

              <div className={activeCategory === null ? 'animate-fade-in' : 'hidden'}>
                <HomeHub
                  onOpenGifts={handleOpenGifts}
                  onOpenStars={handleOpenStars}
                  onOpenPremium={handleOpenPremium}
                  onOpenRent={startRent}
                />
              </div>

              <div className={activeCategory === 'gifts' ? 'animate-fade-in' : 'hidden'}>
                <CategoryScreen
                  title="Архивные подарки"
                  onBack={() => setActiveCategory(null)}
                  showBackButton={false}
                >
                  <GiftsGrid
                    gifts={gifts}
                    isLoading={isLoading}
                    error={giftsError}
                    onOpen={startPurchase}
                    onPreload={handlePreloadOrderForm}
                  />
                </CategoryScreen>
              </div>
            </div>

            <div className={!isFlowActive && activeTab === 'earn' ? 'animate-fade-in' : 'hidden'}>
              <EarnPage
                user={userProfile}
                loading={isUserDataLoading}
                error={profileError}
              />
            </div>

            <div className={!isFlowActive && activeTab === 'profile' ? 'animate-fade-in' : 'hidden'}>
              <ProfilePage
                user={userProfile}
                loading={isUserDataLoading}
                error={profileError}
                onTopUp={handleTopUp}
                onOpenAbout={handleOpenAbout}
                onOpenHistory={handleOpenHistory}
                onOpenPromo={handleOpenPromo}
              />
            </div>

            {topFlow?.type === 'order' ? (
              <FlowScreenContainer onBack={handleFlowBack}>
                <Suspense fallback={<FlowLoading />}>
                  <OrderForm
                    key={topFlow.gift.id}
                    gift={topFlow.gift}
                    selfUsername={selfUsername}
                    submitError={submitError}
                    onBack={handleFlowBack}
                    onSubmit={handlePurchase}
                  />
                </Suspense>
              </FlowScreenContainer>
            ) : null}

            {topFlow?.type === 'stars-order' ? (
              <FlowScreenContainer onBack={handleFlowBack}>
                <Suspense fallback={<FlowLoading />}>
                  <StarsOrderForm
                    selfUsername={selfUsername}
                    submitError={submitError}
                    onBack={handleFlowBack}
                    onSubmit={handleStarsPurchase}
                  />
                </Suspense>
              </FlowScreenContainer>
            ) : null}

            {topFlow?.type === 'premium-order' ? (
              <FlowScreenContainer onBack={handleFlowBack}>
                <Suspense fallback={<FlowLoading />}>
                  <PremiumOrderForm
                    selfUsername={selfUsername}
                    submitError={submitError}
                    onBack={handleFlowBack}
                    onSubmit={handlePremiumPurchase}
                  />
                </Suspense>
              </FlowScreenContainer>
            ) : null}

            {topFlow?.type === 'rent-hub' ? (
              <FlowScreenContainer onBack={handleFlowBack}>
                <Suspense fallback={<FlowLoading />}>
                  <RentHubScreen
                    onOpenGiftRent={handleOpenGiftRent}
                    onOpenUsernameRent={handleOpenUsernameRent}
                    onOpenNumberRent={handleOpenNumberRent}
                    onOpenManage={handleOpenManageRentals}
                  />
                </Suspense>
              </FlowScreenContainer>
            ) : null}

            {topFlow?.type === 'my-rentals' ? (
              <FlowScreenContainer onBack={handleFlowBack}>
                <Suspense fallback={<FlowLoading />}>
                  <MyRentalsScreen />
                </Suspense>
              </FlowScreenContainer>
            ) : null}

            {topFlow?.type === 'rent-catalog' ? (
              <FlowScreenContainer onBack={handleFlowBack}>
                <Suspense fallback={<FlowLoading />}>
                  <RentCatalogScreen onOpenCollection={handleOpenCollection} />
                </Suspense>
              </FlowScreenContainer>
            ) : null}

            {topFlow?.type === 'rent-collection-gifts' ? (
              <FlowScreenContainer onBack={handleFlowBack}>
                <Suspense fallback={<FlowLoading />}>
                  <RentCollectionGiftsScreen
                    key={topFlow.collection?.address}
                    collection={topFlow.collection}
                    onOpenGift={handleOpenRentDetail}
                  />
                </Suspense>
              </FlowScreenContainer>
            ) : null}

            {topFlow?.type === 'rent-detail' ? (
              <FlowScreenContainer onBack={handleFlowBack}>
                <Suspense fallback={<FlowLoading />}>
                  <RentDetailScreen
                    key={topFlow.gift?.nft_address}
                    gift={topFlow.gift}
                    submitError={submitError}
                    onCreateOrder={handleCreateRentOrder}
                  />
                </Suspense>
              </FlowScreenContainer>
            ) : null}

            {topFlow?.type === 'rent-pay' ? (
              <FlowScreenContainer onBack={handleFlowBack}>
                <Suspense fallback={<FlowLoading />}>
                  <RentPayScreen
                    key={topFlow.rentOrder?.orderId}
                    order={topFlow.rentOrder}
                    balance={balance}
                    onSuccess={handleRentSuccess}
                    onTopUp={handleOpenTopUpForRent}
                    onBack={handleFlowBack}
                  />
                </Suspense>
              </FlowScreenContainer>
            ) : null}

            {topFlow?.type === 'rent-usernames' ? (
              <FlowScreenContainer onBack={handleFlowBack}>
                <Suspense fallback={<FlowLoading />}>
                  <UsernameCatalogScreen onOpenUsername={handleOpenUsernameDetail} />
                </Suspense>
              </FlowScreenContainer>
            ) : null}

            {topFlow?.type === 'rent-username-detail' ? (
              <FlowScreenContainer onBack={handleFlowBack}>
                <Suspense fallback={<FlowLoading />}>
                  <UsernameDetailScreen
                    key={topFlow.item?.nft_address}
                    item={topFlow.item}
                    submitError={submitError}
                    onCreateOrder={handleCreateUsernameOrder}
                  />
                </Suspense>
              </FlowScreenContainer>
            ) : null}

            {topFlow?.type === 'rent-username-pay' ? (
              <FlowScreenContainer onBack={handleFlowBack}>
                <Suspense fallback={<FlowLoading />}>
                  <RentPayScreen
                    key={topFlow.rentOrder?.orderId}
                    order={topFlow.rentOrder}
                    balance={balance}
                    payEndpoint="/api/pay-username-rent-from-balance"
                    onSuccess={handleRentSuccess}
                    onTopUp={handleOpenTopUpForRent}
                    onBack={handleFlowBack}
                  />
                </Suspense>
              </FlowScreenContainer>
            ) : null}

            {topFlow?.type === 'rent-numbers' ? (
              <FlowScreenContainer onBack={handleFlowBack}>
                <Suspense fallback={<FlowLoading />}>
                  <NumberCatalogScreen onOpenNumber={handleOpenNumberDetail} />
                </Suspense>
              </FlowScreenContainer>
            ) : null}

            {topFlow?.type === 'rent-number-detail' ? (
              <FlowScreenContainer onBack={handleFlowBack}>
                <Suspense fallback={<FlowLoading />}>
                  <NumberDetailScreen
                    key={topFlow.item?.nft_address}
                    item={topFlow.item}
                    submitError={submitError}
                    onCreateOrder={handleCreateNumberOrder}
                  />
                </Suspense>
              </FlowScreenContainer>
            ) : null}

            {topFlow?.type === 'rent-number-pay' ? (
              <FlowScreenContainer onBack={handleFlowBack}>
                <Suspense fallback={<FlowLoading />}>
                  <RentPayScreen
                    key={topFlow.rentOrder?.orderId}
                    order={topFlow.rentOrder}
                    balance={balance}
                    payEndpoint="/api/pay-number-rent-from-balance"
                    onSuccess={handleRentSuccess}
                    onTopUp={handleOpenTopUpForRent}
                    onBack={handleFlowBack}
                  />
                </Suspense>
              </FlowScreenContainer>
            ) : null}

            {topFlow?.type === 'topup' ? (
              <FlowScreenContainer onBack={handleFlowBack}>
                <Suspense fallback={<FlowLoading />}>
                  <TopUpModal
                    key={topFlow.mode === 'order' ? 'order-payment' : 'top-up-modal'}
                    mode={topFlow.mode}
                    orderId={topFlow.paymentOrder?.orderId ?? null}
                    totalPrice={topFlow.paymentOrder?.totalPrice ?? 0}
                    paymentProduct={topFlow.paymentOrder?.product ?? null}
                    balance={balance}
                    orderError={topFlow.paymentOrder?.error ?? null}
                    paymentMethods={paymentMethods}
                    paymentMethodsLoading={paymentMethodsLoading}
                    paymentMethodsError={paymentMethodsError}
                    onBack={handleFlowBack}
                    onPaid={handleTopUpPaid}
                    onSuccess={handleTopUpSuccess}
                  />
                </Suspense>
              </FlowScreenContainer>
            ) : null}

            {topFlow?.type === 'success' ? (
              <FlowScreenContainer onBack={resetFlow}>
                <Suspense fallback={<FlowLoading />}>
                  <OrderSuccess
                    giftName={topFlow.giftName}
                    product={topFlow.product}
                    starsAmount={topFlow.starsAmount}
                    premiumMonths={topFlow.premiumMonths}
                    recipientUsername={topFlow.recipientUsername}
                    rentDurationDays={topFlow.rentDurationDays}
                    rentAnimationUrl={topFlow.rentAnimationUrl}
                    rentIconUrl={topFlow.rentIconUrl}
                    rentType={topFlow.rentType}
                    onBack={resetFlow}
                    onBuyMore={topFlow.product === 'rent' ? null : () => handleSuccessBuyMore(topFlow.product)}
                    onManageRentals={handleSuccessManageRentals}
                  />
                </Suspense>
              </FlowScreenContainer>
            ) : null}

            {topFlow?.type === 'about' ? (
              <FlowScreenContainer onBack={handleFlowBack}>
                <AboutScreen
                  links={{
                    support_link: userProfile?.support_link,
                    channel_link: userProfile?.channel_link,
                    policy_url: userProfile?.policy_url,
                    agreement_url: userProfile?.agreement_url,
                  }}
                />
              </FlowScreenContainer>
            ) : null}

            {topFlow?.type === 'history' ? (
              <FlowScreenContainer onBack={handleFlowBack}>
                <HistoryScreen />
              </FlowScreenContainer>
            ) : null}

            {topFlow?.type === 'promo' ? (
              <FlowScreenContainer onBack={handleFlowBack}>
                <PromoScreen onSuccess={handlePromoSuccess} />
              </FlowScreenContainer>
            ) : null}

          </div>

          {shouldShowPublicNav({
            isDataLoaded,
            isFlowActive,
            topFlowType: topFlow?.type,
            isRentNavFlow,
            isArchiveGifts,
            showAdmin,
          }) ? (
            <NavBar
              activeTab={
                topFlow?.type === 'about'
                  ? 'profile'
                  : isRentNavFlow
                    ? 'gifts'
                    : activeTab
              }
              onChange={handleTabChange}
            />
          ) : null}

          {showAdmin ? (
            <Suspense fallback={<FlowLoading />}>
              <AdminGate onClose={() => setShowAdmin(false)} />
            </Suspense>
          ) : null}
        </div>
      )}
    </ToastProvider>
  )
}
